<template>
    <pc-input-group>
        <!-- Main  div -->
        <div class="avoid-page-break-inside">
            <pc-label
                v-bind:label="label"
                v-bind:show-required="required"
                v-bind:show-help="showHelp"
                v-bind:help-link="showHelpLinkOnly"
                class="avoid-page-break-after"
                v-on:help-clicked="emitHelpClicked" />
            <div class="avoid-page-break-after">
                <slot name="help" />
            </div>
            <div
                v-bind:class="responsiveClasses.systemWrapper"
                class="mt-3">
                <transition-group
                    tag="span"
                    v-bind:css="false"
                    v-on:enter="enter"
                    v-on:leave="leave">
                    <energystoragesubsystem
                        v-for="subsystem in subsystems"
                        v-bind:id="subsystem.id"
                        v-bind:ref="subsystem.id"
                        v-bind:key="subsystem.id"
                        v-bind:element="element"
                        v-bind:energystoragesubsystem="subsystem"
                        v-bind:readonly="readonly"
                        v-on:copy-invbatt-subsystem="copyInvBattSubsystem"
                        v-on:copy-subsystem-parent="copySubsystemParent" />
                </transition-group>
                <pc-button-row>
                    <pc-button
                        v-bind:disabled="readonly"
                        data-test-role="add-integrated-energy-storage-btn"
                        v-on:click="addEmptySubsystemIntegratedEnergyStorage">
                        {{ addIntegratedEnergyStorageButtonText }}
                    </pc-button>
                    <pc-button
                        v-bind:disabled="readonly"
                        data-test-role="add-inverter-and-battery-btn"
                        v-on:click="addEmptySubsystemInverterAndBattery">
                        {{ addInverterAndBatteryButtonText }}
                    </pc-button>
                </pc-button-row>
            </div>
        </div>
        <hr />
        <!-- Calculated Output div -->
        <div class="avoid-page-break-inside">
            <div class="row gx-3">
                <div v-bind:class="responsiveClasses.outputWrapper">
                    <div
                        v-for="output in systemOutputs"
                        v-bind:key="output.label"
                        v-bind:class="responsiveClasses.outputRowWrapper"
                        class="row">
                        <div
                            v-bind:class="responsiveClasses.outputRow"
                            class="fw-bold">
                            {{ output.label }}
                        </div>
                        <div v-bind:class="responsiveClasses.outputRow">
                            <template v-if="hasCalculatedOutput">
                                <span
                                    v-for="(value, index) in output.values"
                                    v-bind:key="index"
                                    v-bind:data-test-role="value.testRole">
                                    <template v-if="index > 0"> / </template>
                                    {{ value.value }}
                                    {{ value.unit }}
                                </span>
                            </template>
                            <template v-else>
                                <span
                                    v-if="isDirty"
                                    class="fw-bold text-danger">
                                    {{ needsRecalculatedMessage }}
                                </span>
                                <span v-else>
                                    {{ notYetCalculatedMessage }}
                                </span>
                            </template>
                        </div>
                    </div>
                </div>
                <div
                    v-bind:class="responsiveClasses.calculateRow"
                    class="col d-flex align-items-start">
                    <pc-loader
                        v-bind:loading="calculating"
                        data-test-role="calculate-btn-loader"
                        small>
                        <pc-button
                            v-bind:disabled="readonly"
                            data-test-role="calculate-btn"
                            v-on:click="calculateOutput">
                            {{ calculateButtonText }}
                        </pc-button>
                    </pc-loader>
                </div>
            </div>
            <!-- Output Error div -->
            <div class="d-flex flex-column align-items-center">
                <transition
                    v-bind:css="false"
                    v-on:enter="enter"
                    v-on:leave="leave">
                    <div
                        v-if="outputError"
                        style="max-width: 100%"
                        class="fw-bold text-danger">
                        {{ outputError }}
                    </div>
                </transition>
            </div>
        </div>
        <hr />
    </pc-input-group>
</template>

<script>
    import base, { mapGroupIdsAndValues } from "./_elementBase";
    import {
        energyStorageSystemController,
        SUFFIXES,
        ENERGY_STORAGE_SUBSYSTEM_TYPE
    } from "Forms/Components/ProjectForm/ProjectElements/energyStorageSystemElementController";
    import energystoragesubsystem from "./energystoragesystem/energystoragesubsystem.vue";
    import { SLIDE_DOWN_UP } from "Util/animations";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/energystoragesystem.i18n";
    import { ValidationHub } from "Validation/validationHub";
    import { PROJECT_FORM_TYPES } from "../ProjectForm/projectFormStore";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        systemWrapper: {
            // ms-2 ms-sm-5
            ms: { xs: 2, sm: 5 }
        },
        outputWrapper: {
            // order-2 order-md-1 col-12 col-md-8 col-lg-6
            order: { xs: 2, md: 1 },
            col: { xs: 12, md: 8, lg: 6 }
        },
        outputRowWrapper: {
            // mb-2 mb-sm-0
            mb: { xs: 2, sm: 0 }
        },
        outputRow: {
            // col-12 col-sm-6
            col: { xs: 12, sm: 6 }
        },
        calculateRow: {
            // order-1 order-md-2 justify-content-md-around
            order: { xs: 1, md: 2 },
            "justify-content": { md: "around" }
        }
    };

    export default {
        components: { energystoragesubsystem },
        extends: base,
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        provide() {
            return {
                energyStorageElement: this,
                validationHub: this.validationHub
            };
        },
        data() {
            return {
                calculating: false,
                outputError: null,
                validationEnabled: true,
                validationHub: new ValidationHub()
            };
        },
        computed: {
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            subsystems() {
                if (this.isProjectFormModuleRegistered()) {
                    return energyStorageSystemController.computeSubsystems(this.element, this.$store.getters);
                }
                return [];
            },
            systemOutputs() {
                const outputs = [];

                const energyStorageSystemData = this.$serviceControllers.EnergyStorageSystem;
                if (!energyStorageSystemData || !energyStorageSystemData.EquipmentCalculations) {
                    return outputs;
                }

                energyStorageSystemData.EquipmentCalculations.forEach((calc) => {
                    let label = calc.FieldLabel;
                    switch (calc.FieldName) {
                        case "EnergyCalculation":
                            label = this.energyCalculationDisplayName;
                            break;
                        case "PowerCalculation":
                            label = this.powerCalculationDisplayName;
                            break;
                    }
                    let value = "";
                    if (this[calc.FieldName]) {
                        value = Number(this[calc.FieldName]).toFixed(calc.RoundingDigits);
                    }
                    const testRole = calc.FieldLabel.trim().replaceAll(" ", "-").toLowerCase() + "-output";
                    outputs.push({
                        label: label + ":",
                        values: [
                            {
                                value: value,
                                unit: calc.Units,
                                testRole: testRole
                            }
                        ]
                    });
                });

                outputs.forEach((out) => {
                    out.values = out.values.filter((v) => Boolean(v.value));
                });

                return outputs;
            },
            isDirty() {
                return this.projectElement && this.projectElement.auxState.isDirty;
            },
            hasCalculatedOutput() {
                const energyStorageSystem = this.$serviceControllers.EnergyStorageSystem;
                const self = this;
                return energyStorageSystem.EquipmentCalculations.some(function (calc) {
                    return self[calc.FieldName] && self[calc.FieldName] > 0;
                });
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            ...mapGroupIdsAndValues(SUFFIXES.outputFields),
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            cloneSubsystems() {
                return JSON.parse(JSON.stringify(this.subsystems));
            },
            addEmptySubsystemIntegratedEnergyStorage() {
                this.addEmptySubsystem(ENERGY_STORAGE_SUBSYSTEM_TYPE.IntegratedEnergyStorageSystem);
            },
            addEmptySubsystemInverterAndBattery() {
                this.addEmptySubsystem(ENERGY_STORAGE_SUBSYSTEM_TYPE.InverterAndBattery);
            },
            addEmptySubsystem(energyStorageSubsystemType) {
                const clone = this.cloneSubsystems();
                clone.push(energyStorageSystemController.getEmptySubsystem(energyStorageSubsystemType));
                this.setEnergyStorageConfig(clone);
            },
            addEmptyChild(energyStorageSubsystemId, equipmentType) {
                const clone = this.cloneSubsystems();
                const index = clone.findIndex((subsystem) => subsystem.id === energyStorageSubsystemId);
                const subsystem = clone[index];
                if (subsystem) {
                    const equipmentItem = energyStorageSystemController.getEmptyChild(equipmentType);
                    equipmentItem.assignedParent = index;
                    subsystem.childGroups.push(equipmentItem);
                }
                this.setEnergyStorageConfig(clone);
            },
            setParentItemProperty({ subsystemId, name, value }) {
                const clone = this.cloneSubsystems();
                const subsystem = this.findSubsystem(clone, subsystemId);
                if (subsystem) {
                    subsystem[name] = value;
                }
                this.setEnergyStorageConfig(clone);
            },
            setEquipmentItemProperty({ subsystemId, equipmentItemId, name, value }) {
                const clone = this.cloneSubsystems();
                const equipmentItem = this.findEquipmentItem(clone, subsystemId, equipmentItemId);
                if (equipmentItem) {
                    equipmentItem[name] = value;
                }
                this.setEnergyStorageConfig(clone);
            },
            findSubsystem(subsystems, subsystemId) {
                return subsystems.find((subsystem) => subsystem.id === subsystemId);
            },
            findEquipmentItem(subsystems, subsystemId, equipmentItemId) {
                const subsystem = this.findSubsystem(subsystems, subsystemId);
                if (subsystem) {
                    return subsystem.childGroups.find((equipmentItem) => equipmentItem.id === equipmentItemId);
                }
                return null;
            },
            setEnergyStorageConfig(subsystems) {
                this.outputError = null;
                if (this.isProjectFormModuleRegistered()) {
                    this.$store.dispatch(PROJECT_FORM_TYPES.actions.setProjectEnergyStorageConfig, {
                        elementId: this.element.PublicId,
                        energyStorageConfig: subsystems
                    });
                }
            },
            deleteSubsystem(energyStorageSubsystemId) {
                if (this.subsystems.length < 1) {
                    return;
                }
                const clone = this.cloneSubsystems();
                const index = clone.findIndex((subsystem) => subsystem.id === energyStorageSubsystemId);
                clone.splice(index, 1);
                this.setEnergyStorageConfig(clone);
            },
            deleteEquipmentItem({ subsystemId, equipmentItemId }) {
                const clone = this.cloneSubsystems();
                const subsystem = this.findSubsystem(clone, subsystemId);
                if (subsystem) {
                    const equipmentItemIndex = subsystem.childGroups.findIndex(
                        (equipmentItem) => equipmentItem.id === equipmentItemId
                    );
                    subsystem.childGroups.splice(equipmentItemIndex, 1);
                }
                this.setEnergyStorageConfig(clone);
            },
            async calculateOutput() {
                this.outputError = null;
                this.validationHub.validate();

                if (this.isProjectFormModuleRegistered()) {
                    try {
                        this.calculating = true;
                        this.outputError = null;
                        const result = await this.$store.dispatch(PROJECT_FORM_TYPES.actions.calcEnergyStorageOutput, {
                            elementId: this.element.PublicId
                        });
                        if (result.hasError) {
                            this.outputError = result.error;
                        }
                    } finally {
                        this.calculating = false;
                    }
                }
            },
            async copyInvBattSubsystem(subsystemId) {
                const clone = this.cloneSubsystems();
                const subsystemIndex = clone.findIndex((subsystem) => subsystem.id === subsystemId);
                if (subsystemIndex !== -1) {
                    const subsystem = clone[subsystemIndex];
                    const newSubsystem = energyStorageSystemController.getEmptySubsystem(this.element);
                    newSubsystem.subsystemType = subsystem.subsystemType;
                    newSubsystem.manufacturerId = subsystem.manufacturerId;
                    newSubsystem.modelId = subsystem.modelId;
                    // Loop through child groups and copy them to the newSubsystem
                    subsystem.childGroups.forEach((childItem) => {
                        const newChildItem = energyStorageSystemController.getEmptyChild(childItem.equipmentType);
                        newChildItem.assignedParent = childItem.assignedParent + 1;
                        newChildItem.manufacturerId = childItem.manufacturerId;
                        newChildItem.modelId = childItem.modelId;
                        newSubsystem.childGroups.push(newChildItem);
                    });
                    // Insert new subsystem immediately after the copied subsystem
                    clone.splice(subsystemIndex + 1, 0, newSubsystem);
                    // Update index property of each subsystem
                    clone.forEach((subsystem) => {
                        subsystem.index = clone.indexOf(subsystem);
                    });
                    this.setEnergyStorageConfig(clone);
                    // Put in a break to allow the UI to update with the ref for the new subsystem
                    await this.$app.timeout(0);
                    // Set the focus to the new subsystem
                    if (this.$refs[newSubsystem.id]) {
                        if (
                            newSubsystem.subsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.IntegratedEnergyStorageSystem
                        ) {
                            this.$refs[newSubsystem.id][0].focusIesSubsystemInput();
                        } else {
                            // subsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.InverterAndBattery
                            this.$refs[newSubsystem.id][0].focusInvBattSubsystemInput();
                        }
                    }
                }
            },
            async copySubsystemParent(subsystemId) {
                const clone = this.cloneSubsystems();
                const subsystemIndex = clone.findIndex((subsystem) => subsystem.id === subsystemId);
                if (subsystemIndex !== -1) {
                    const subsystem = clone[subsystemIndex];
                    const newSubsystem = energyStorageSystemController.getEmptySubsystem(this.element);
                    newSubsystem.subsystemType = subsystem.subsystemType;
                    newSubsystem.manufacturerId = subsystem.manufacturerId;
                    newSubsystem.modelId = subsystem.modelId;
                    // Insert new subsystem immediately after the copied subsystem
                    clone.splice(subsystemIndex + 1, 0, newSubsystem);
                    // Update index property of each subsystem
                    clone.forEach((subsystem) => {
                        subsystem.index = clone.indexOf(subsystem);
                    });
                    this.setEnergyStorageConfig(clone);
                    // Put in a break to allow the UI to update with the ref for the new subsystem
                    await this.$app.timeout(0);
                    // Set the focus to the new subsystem
                    if (this.$refs[newSubsystem.id]) {
                        if (
                            newSubsystem.subsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.IntegratedEnergyStorageSystem
                        ) {
                            this.$refs[newSubsystem.id][0].focusIesSubsystemInput();
                        } else {
                            // subsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.InverterAndBattery
                            this.$refs[newSubsystem.id][0].focusInvBattSubsystemInput();
                        }
                    }
                }
            },
            async copyEquipmentGroup(subsystemId, equipmentId) {
                const clone = this.cloneSubsystems();
                const subsystemIndex = clone.findIndex((subsystem) => subsystem.id === subsystemId);
                if (subsystemIndex !== -1) {
                    const subsystem = clone[subsystemIndex];
                    if (subsystem) {
                        const equipmentIndex = subsystem.childGroups.findIndex(
                            (equipmentItem) => equipmentItem.id === equipmentId
                        );
                        if (equipmentIndex !== -1) {
                            const equipmentItem = subsystem.childGroups[equipmentIndex];
                            const newEquipmentItem = energyStorageSystemController.getEmptyChild(
                                equipmentItem.equipmentType
                            );
                            newEquipmentItem.assignedParent = subsystemIndex;
                            newEquipmentItem.manufacturerId = equipmentItem.manufacturerId;
                            newEquipmentItem.modelId = equipmentItem.modelId;
                            // Insert newEquipmentItem immediately after the copied equipmentItem
                            subsystem.childGroups.splice(equipmentIndex + 1, 0, newEquipmentItem);
                            // Update index property of each equipmentItem
                            subsystem.childGroups.forEach((equipmentItem) => {
                                equipmentItem.index = subsystem.childGroups.indexOf(equipmentItem);
                            });
                            this.setEnergyStorageConfig(clone);
                            return newEquipmentItem.id;
                        }
                    }
                }
            },
            enter: SLIDE_DOWN_UP.enter,
            leave: SLIDE_DOWN_UP.leave
        }
    };
</script>
